import { CLEANING_STATUS } from "@/lib/constants";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUS_COLORS, CLEANING_RING, SEVERITY, PLATFORM_META, PROBLEM_STATUSES, CATEGORIES, PIECES, APT_STATUSES, APT_STATUS_META } from "@/lib/constants";
import { ArrowLeft, MapPin, Users, Bed, Wifi, Snowflake, KeyRound, ExternalLink, Plus, AlertTriangle, Pencil, FolderOpen, CalendarPlus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MultiPhotoUpload } from "@/components/PhotoUpload";
import AccessStepsEditor from "@/components/AccessStepsEditor";
import ApartmentWizard from "@/components/ApartmentWizard";
import { CheckCircle2, Clock } from "lucide-react";

function ResolveDialog({ problem, onClose, onResolved }) {
  const [stage, setStage] = useState("q1");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStage("q1"); setSaving(false); }, [problem]);
  if (!problem) return null;
  const isSevere = ["majeur", "critique"].includes(problem.severity);

  const finalize = async (patch, msg) => {
    setSaving(true);
    try {
      await api.put(`/problems/${problem.id}`, { ...problem, ...patch });
      toast.success(msg);
      onResolved();
    } catch { toast.error("Erreur lors de la mise à jour"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!problem} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="resolve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />Résoudre l'anomalie
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm font-semibold text-slate-900">{problem.room || problem.title}</div>
        <div className="text-xs text-slate-500 mb-2">{problem.category} — {SEVERITY[problem.severity]?.label}</div>

        {stage === "q1" && !isSevere && (
          <>
            <p className="text-sm text-slate-600">
              Le problème a-t-il été entièrement corrigé ? Si oui, l'anomalie sera marquée comme résolue
              et disparaîtra des anomalies actives.
            </p>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={onClose} data-testid="resolve-cancel">Non</Button>
              <Button onClick={() => finalize({ status: "Résolu" }, "Anomalie résolue et retirée")}
                      disabled={saving} data-testid="resolve-confirm"
                      className="bg-blue-600 hover:bg-blue-700 text-white">Oui, entièrement corrigé</Button>
            </DialogFooter>
          </>
        )}

        {stage === "q1" && isSevere && (
          <>
            <p className="text-sm text-slate-600">
              {problem.severity === "critique"
                ? "Cette anomalie est critique et marque l'appartement comme inactif. Le problème a-t-il été corrigé ?"
                : "Cette anomalie est majeure et colore l'appartement en rouge. Le problème a-t-il été corrigé ?"}
            </p>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={() => setStage("q2")} data-testid="resolve-partial">Partiellement</Button>
              <Button onClick={() => finalize({ status: "Résolu" }, "Anomalie résolue et retirée")}
                      disabled={saving} data-testid="resolve-full"
                      className="bg-blue-600 hover:bg-blue-700 text-white">Entièrement</Button>
            </DialogFooter>
          </>
        )}

        {stage === "q2" && (
          <>
            <button onClick={() => setStage("q1")} data-testid="resolve-back"
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors mb-1">
              <ArrowLeft className="w-3.5 h-3.5" />Retour
            </button>
            <p className="text-sm text-slate-600 flex items-start gap-2">
              <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              Un passage est-il prévu pour finaliser la correction ?
            </p>
            <p className="text-xs text-slate-400">
              Oui → l'anomalie reste <strong>majeure</strong>. Non → elle est rétrogradée en <strong>mineure</strong>.
            </p>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" disabled={saving} data-testid="resolve-no-visit"
                      onClick={() => finalize({ severity: "mineur", status: "À traiter" }, "Anomalie rétrogradée en mineure")}>
                Non
              </Button>
              <Button disabled={saving} data-testid="resolve-visit-planned"
                      onClick={() => finalize({ severity: "majeur", status: "À traiter" }, "Anomalie maintenue en majeure")}
                      className="bg-blue-600 hover:bg-blue-700 text-white">Oui, passage prévu</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

export function ProblemForm({ apt, site, onCreated, testid = "signal-problem-btn" }) {
  const isSite = !!site;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ room: "Salon", title: "", description: "", category: "Plomberie", severity: "mineur" });
  const [photos, setPhotos] = useState([]);
  const submit = async () => {
    if (isSite && !form.title.trim()) { toast.error("Le titre est requis"); return; }
    try {
      const payload = isSite
        ? { site_id: site.id, title: form.title.trim(), room: null, description: form.description, category: form.category, severity: form.severity, photos }
        : { apartment_id: apt.id, title: `${form.room} — ${form.category}`, room: form.room, description: form.description, category: form.category, severity: form.severity, photos };
      await api.post("/problems", payload);
      toast.success("Anomalie signalée");
      setOpen(false);
      setForm({ room: "Salon", title: "", description: "", category: "Plomberie", severity: "mineur" });
      setPhotos([]);
      onCreated();
    } catch (e) {
      toast.error("Erreur lors du signalement");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={testid} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Plus className="w-4 h-4 mr-1.5" />Signaler une anomalie
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Signaler une anomalie{isSite ? ` — ${site.name}` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isSite && (
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Ex. Fuite d'eau, serrure cassée…" data-testid="problem-title-input" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!isSite && (
            <div>
              <Label>Pièce</Label>
              <Select value={form.room} onValueChange={(v) => setForm({ ...form, room: v })}>
                <SelectTrigger data-testid="problem-room-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{PIECES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="problem-cat-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Gravité</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="problem-sev-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mineur">Mineure</SelectItem>
                <SelectItem value="majeur">Majeure</SelectItem>
                <SelectItem value="critique">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Ex : Le robinet fuit en permanence."
                      data-testid="problem-desc-input" />
          </div>
          <div>
            <Label>Photos</Label>
            <div className="mt-1">
              <MultiPhotoUpload photos={photos} onChange={setPhotos} testid="problem-photos" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} data-testid="submit-problem-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApartmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "problems" ? "problems" : "info";
  const [apt, setApt] = useState(null);
  const [problems, setProblems] = useState([]);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [editingDrive, setEditingDrive] = useState(false);
  const [driveId, setDriveId] = useState("");
  const { user } = useAuth();

  const load = () => {
    api.get(`/apartments/${id}`).then((r) => setApt(r.data)).catch(() => {});
    api.get(`/problems?include_resolved=true`).then((r) => setProblems(r.data.filter((p) => p.apartment_id === id))).catch(() => {});
  };
  useEffect(load, [id]);

  if (!apt) return <div className="p-8 text-slate-500">Chargement…</div>;
  const status = STATUS_COLORS[apt.color_status] || STATUS_COLORS.ok;
  const clean = CLEANING_RING[apt.cleaning_company];
  const openProbs = problems.filter((p) => p.status !== "Résolu");
  const canEdit = ["admin", "account_manager", "assistant_manager"].includes(user?.role);
  const isCleaning = user?.role === "cleaning";

  const addToTour = async (p) => {
    try {
      const { data } = await api.post("/tours/add-stop", { apartment_id: apt.id, problem_ids: [p.id] });
      if (data.status === "en_attente_approbation")
        toast.info("Tournée en cours — intervention mise en attente d'approbation");
      else
        toast.success(data.merged ? "Anomalie ajoutée à l'étape existante du planning" : "Intervention ajoutée au planning du jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const changeCleaningStatus = async (v) => {
    try {
      await api.patch(`/apartments/${apt.id}/cleaning-status`, { cleaning_status: v });
      toast.success("Statut ménage mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateProblemStatus = async (p, newStatus) => {
    await api.put(`/problems/${p.id}`, { ...p, status: newStatus });
    toast.success("Statut mis à jour");
    load();
  };

  const handleStatusChange = (p, v) => {
    if (v === "Résolu") setResolveTarget(p);
    else updateProblemStatus(p, v);
  };

  const changeAptStatus = async (v) => {
    try {
      await api.put(`/apartments/${apt.id}`, { ...apt, status: v });
      toast.success("Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveDriveId = async () => {
    try {
      await api.put(`/apartments/${apt.id}`, { ...apt, drive_folder_id: driveId.trim() });
      toast.success("ID dossier Drive enregistré");
      setEditingDrive(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
              data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" />Retour
      </button>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm mb-6">
        {apt.photo_url && <img src={apt.photo_url} alt={apt.name} className="w-full h-56 md:h-72 object-cover" />}
        <div className="p-5 md:p-7">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900">{apt.name}</h1>
              <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1">
                <MapPin className="w-4 h-4" />{apt.address}, {apt.city} {apt.arrondissement}
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <ApartmentWizard apartment={apt} onSaved={load} />
                <Select value={apt.status} onValueChange={changeAptStatus}>
                  <SelectTrigger className="w-40" data-testid="apt-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isCleaning && (
              <Select value={apt.cleaning_status || "pas_fait"} onValueChange={changeCleaningStatus}>
                <SelectTrigger className="w-44" data-testid="cleaning-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CLEANING_STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`cleaning-status-${k}`}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-4">
            <Badge className={`${status.cls} border`}>{status.label}</Badge>
            <Badge className={`${APT_STATUS_META[apt.status]?.cls || ""} border`} data-testid="apt-status-badge">
              <span className="w-1.5 h-1.5 rounded-full mr-1" style={{ background: APT_STATUS_META[apt.status]?.dot }} />{apt.status}
            </Badge>
            {apt.mobility_lease && <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 border">Bail mobilité</Badge>}
            {apt.key_at_office && <Badge className="bg-sky-50 text-sky-700 border-sky-200 border" data-testid="key-office-badge">Clé au bureau{apt.key_office_box ? ` — boîte n° ${apt.key_office_box}` : ""}</Badge>}
            {openProbs.length > 0 && (
              <Badge className="bg-red-50 text-red-700 border-red-200 border">
                <AlertTriangle className="w-3 h-3 mr-1" />{openProbs.length} anomalie{openProbs.length > 1 ? "s" : ""}
              </Badge>
            )}
            <Badge className={`${clean?.badge} border`}>{apt.cleaning_company}</Badge>
            {apt.referent && <Badge variant="outline">Réf. {apt.referent}</Badge>}
            <Badge className={`${(CLEANING_STATUS[apt.cleaning_status || "pas_fait"]).cls} border`} data-testid="cleaning-status-badge">
              Ménage : {(CLEANING_STATUS[apt.cleaning_status || "pas_fait"]).label}
            </Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="info" data-testid="tab-info">Informations</TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access">Accès</TabsTrigger>
          <TabsTrigger value="problems" data-testid="tab-problems">Anomalies</TabsTrigger>
          <TabsTrigger value="platforms" data-testid="tab-platforms">Plateformes</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold font-display text-slate-900 mb-3">Général</h3>
              <InfoRow label="Nom interne" value={apt.name} />
              <InfoRow label="Adresse" value={apt.address} />
              <InfoRow label="Ville" value={apt.city} />
              <InfoRow label="Code postal" value={apt.postal_code} />
              <InfoRow label="Arrondissement" value={apt.arrondissement} />
              <InfoRow label="Département" value={apt.department} />
              <InfoRow label="Région" value={apt.region} />
              <InfoRow label="Pays" value={apt.country} />
              <InfoRow label="Étage" value={apt.floor} />
              <InfoRow label="Ascenseur" value={apt.elevator ? "Oui" : "Non"} />
              <InfoRow label="Surface" value={apt.surface_m2 ? `${apt.surface_m2} m²` : "—"} />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold font-display text-slate-900 mb-3">Capacité & équipements</h3>
              <InfoRow label="Pièces" value={apt.rooms} />
              <InfoRow label="Chambres" value={apt.bedrooms} />
              <InfoRow label="Salles de bain" value={apt.bathrooms} />
              <InfoRow label="WC" value={apt.wc} />
              <InfoRow label="Capacité max" value={<span className="flex items-center gap-1 justify-end"><Users className="w-3.5 h-3.5" />{apt.max_guests}</span>} />
              <InfoRow label="Couchages" value={<span className="flex items-center gap-1 justify-end"><Bed className="w-3.5 h-3.5" />{apt.sleepings}</span>} />
              <InfoRow label="Lits" value={`${apt.beds_double || 0} double(s) · ${apt.beds_single || 0} simple(s) · ${apt.beds_sofa || 0} canapé(s)-lit`} />
              <InfoRow label="Wi-Fi" value={apt.wifi_available ? "Oui" : "Non"} />
              <InfoRow label="Climatisation" value={apt.ac_available ? (apt.ac_type || "Oui") : "Non"} />
              <InfoRow label="Bail mobilité" value={apt.mobility_lease ? "Oui" : "Non"} />
            </div>
            {canEdit && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm md:col-span-2" data-testid="gestion-section">
              <h3 className="font-bold font-display text-slate-900 mb-3">Gestion</h3>
              <div className="grid md:grid-cols-2 gap-x-8">
                <InfoRow label="Propriétaire" value={apt.owner_name} />
                <InfoRow label="Référent" value={apt.referent} />
                <InfoRow label="Entreprise ménage" value={apt.cleaning_company} />
                <InfoRow label="Statut" value={apt.status} />
              </div>
              {apt.mobility_lease && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid="drive-section">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-sm text-slate-500 flex items-center gap-1.5">
                      <FolderOpen className="w-4 h-4" />Dossier Drive (bail signé)
                    </span>
                    {!editingDrive ? (
                      <span className="flex items-center gap-2">
                        {apt.drive_folder_id ? (
                          <span className="text-sm font-medium text-slate-800 break-all" data-testid="drive-id-value">{apt.drive_folder_id}</span>
                        ) : (
                          <span className="text-sm font-medium text-red-600" data-testid="drive-id-missing">ID Drive en attente</span>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => { setDriveId(apt.drive_folder_id || ""); setEditingDrive(true); }}
                                  data-testid="drive-id-edit-btn">
                            <Pencil className="w-3 h-3 mr-1" />{apt.drive_folder_id ? "Modifier" : "Renseigner"}
                          </Button>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 flex-1 flex-wrap">
                        <Input value={driveId} onChange={(e) => setDriveId(e.target.value)}
                               placeholder="Collez le lien du dossier Drive (ou juste son ID)"
                               className="h-8 text-xs flex-1 min-w-52" data-testid="drive-id-input" />
                        <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={saveDriveId}
                                data-testid="drive-id-save-btn">Enregistrer</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingDrive(false)}
                                data-testid="drive-id-cancel-btn">Annuler</Button>
                      </span>
                    )}
                  </div>
                  {!apt.drive_folder_id && (
                    <p className="text-[11px] text-red-500 mt-1.5">Obligatoire : sans ID, la création et l'envoi de bail sont bloqués pour cet appartement.</p>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="access" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-4 h-4 text-slate-700" />
                <h3 className="font-bold font-display text-slate-900">Codes d'accès</h3>
              </div>
              <InfoRow label="Remise des clés" value={apt.key_handover} />
              <InfoRow label="Code immeuble" value={apt.building_code} />
              <InfoRow label="Interphone" value={apt.intercom} />
              <InfoRow label="Étage" value={apt.floor} />
              <InfoRow label="Position" value={apt.door_position} />
              <InfoRow label="Boîte à clés" value={apt.keybox ? "Oui" : "Non"} />
              <InfoRow label="Code boîte à clés" value={apt.keybox_code} />
              <InfoRow label="Vigik/Badge" value={apt.vigik} />
              <InfoRow label="Code portail" value={apt.gate_code} />
              <InfoRow label="Code parking" value={apt.parking_code} />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Wifi className="w-4 h-4 text-slate-700" />
                <h3 className="font-bold font-display text-slate-900">Wi-Fi & clés</h3>
              </div>
              <InfoRow label="Wi-Fi disponible" value={apt.wifi_available ? "Oui" : "Non"} />
              <InfoRow label="Réseau" value={apt.wifi_name} />
              <InfoRow label="Mot de passe" value={apt.wifi_password} />
              <InfoRow label="Nombre de clés" value={apt.keys_count} />
              <InfoRow label="2ᵉ clé" value={apt.second_key_location} />
              <InfoRow label="KeyNest" value={apt.keynest ? "Oui" : "Non"} />
              <InfoRow label="Adresse KeyNest" value={apt.keynest_address} />
              <InfoRow label="Code de retrait KeyNest" value={apt.keynest_ref} />
            </div>
          </div>

          <AccessStepsEditor apt={apt} canEdit={canEdit} onSaved={load} />
        </TabsContent>

        <TabsContent value="problems" className="mt-4">
          {user.role !== "read_only" && (
            <div className="flex justify-end mb-3">
              <ProblemForm apt={apt} onCreated={load} />
            </div>
          )}
          <div className="space-y-3">
            {openProbs.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
                Aucune anomalie active
              </div>
            )}
            {openProbs.map((p) => {
              const sev = SEVERITY[p.severity];
              return (
                <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm transition-all hover:border-slate-300"
                     data-testid={`problem-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-slate-900">{p.room || p.title}</span>
                        <Badge className={`${sev.cls} border`}>{sev.label}</Badge>
                        <Badge className="bg-red-50 text-red-700 border-red-200 border status-todo">{p.status}</Badge>
                      </div>
                      <div className="text-sm text-slate-600">{p.description}</div>
                      {p.photos?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {p.photos.map((ph, i) => (
                            <img key={i} src={ph} alt="" className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-slate-400 mt-2">
                        {p.category} • Signalé par {p.reported_by || "—"} • {new Date(p.created_at).toLocaleString("fr-FR")}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Select value={p.status} onValueChange={(v) => handleStatusChange(p, v)}>
                          <SelectTrigger className="w-32" data-testid={`problem-status-${p.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PROBLEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => addToTour(p)}
                                data-testid={`add-to-tour-${p.id}`}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50">
                          <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Planning du jour
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="platforms" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {(apt.platforms || []).length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 col-span-2">
                Aucune plateforme configurée
              </div>
            )}
            {(apt.platforms || []).map((pl, i) => {
              const meta = PLATFORM_META[pl.platform] || { name: pl.platform, color: "#0F172A" };
              return (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-3 h-3 rounded-full" style={{ background: meta.color }} />
                    <div className="font-bold font-display text-slate-900">{meta.name}</div>
                  </div>
                  {pl.external_id && <InfoRow label="ID" value={pl.external_id} />}
                  <a href={pl.url} target="_blank" rel="noreferrer"
                     data-testid={`platform-link-${pl.platform}`}
                     className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-900 font-medium hover:underline">
                    Ouvrir l'annonce <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <ResolveDialog problem={resolveTarget}
                     onClose={() => setResolveTarget(null)}
                     onResolved={() => { setResolveTarget(null); load(); }} />
    </div>
  );
}
